import { Box, Circle, HStack, SimpleGrid } from "@chakra-ui/react";
import {
  Carousel,
  CarouselIconButton,
  CarouselSlide,
  useCarousel,
} from "@ui/components/carousel";
import { useState } from "react";
import { IoChevronBackOutline, IoChevronForwardOutline } from "react-icons/io5";
import { QuickFact, QuickFactProps } from "./quick-fact";
import { QuickFactsRendererProps } from "./renderer";

export interface QuickFactsDesktopCarouselProps
  extends QuickFactsRendererProps {}

function configureSections(facts: QuickFactsRendererProps["facts"]) {
  const sections: any[] = [];
  if (facts?.length <= 1) {
    return [facts];
  } else {
    for (let i = 0; i < facts?.length; i += 3) {
      sections.push(facts.slice(i, i + 3));
    }
    return sections;
  }
}

export const QuickFactsDesktopCarousel = ({
  facts,
}: QuickFactsDesktopCarouselProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const [ref, slider] = useCarousel({
    slideChanged: (slider: any) => setCurrentSlide(slider.track.details.rel),
  });
  const sections = configureSections(facts);
  const hasPrevious = currentSlide !== 0;
  const hasNext = currentSlide < sections?.length - 1;
  return (
    <Box
      position="relative"
      alignSelf={{ md: "center", lg: "stretch" }}
      maxW={{ md: "3xl", lg: "100%" }}
      sx={{
        _hover: {
          "> button": {
            display: "inline-flex",
          },
        },
      }}
    >
      <Carousel ref={ref}>
        {sections?.map((section, idx) => {
          return (
            <CarouselSlide key={idx}>
              <SimpleGrid columns={section.length}>
                {section.map((fact: QuickFactProps, i: number) => (
                  <QuickFact key={i} {...fact} mx="auto" my="6" />
                ))}
              </SimpleGrid>
            </CarouselSlide>
          );
        })}
      </Carousel>
      {hasPrevious && (
        <CarouselIconButton
          pos="absolute"
          left="3"
          top="50%"
          transform="translateY(-50%)"
          hoverColor="darkGreen.600"
          activeColor="darkGreen.600"
          onClick={() => slider.current?.prev()}
          icon={<IoChevronBackOutline />}
          aria-label="Previous Slide"
        />
      )}

      {hasNext && (
        <CarouselIconButton
          pos="absolute"
          right="3"
          top="50%"
          transform="translateY(-50%)"
          hoverColor="darkGreen.600"
          activeColor="darkGreen.600"
          onClick={() => slider.current?.next()}
          icon={<IoChevronForwardOutline />}
          aria-label="Next Slide"
        />
      )}
      <HStack position="absolute" width="full" justify="center" bottom="0">
        {sections?.length > 1 &&
          sections?.map((_, index) => (
            <Circle
              key={index}
              size="2"
              bg={currentSlide === index ? "darkGreen.600" : "darkGreen.200"}
            />
          ))}
      </HStack>
    </Box>
  );
};
